import { useQuery } from 'react-query';

import type { QueryFunction } from '@frontend/api';
import { queryClient, useRequest } from '@frontend/api';

import type {
  DestinationModelClass,
  FieldSuggestion,
  SourceModelClass,
} from './types';

type BaseQueryParams = {
  destinationModelClass?: DestinationModelClass;
  destinationQuoteId?: string;
  destinationPartId?: string;
  fieldName?: string;
  sourceModelClass?: SourceModelClass;
  sourceEmailFileId?: string;
  sourcePartFileId?: string;
};

export type GetSuggestionsQueryParams = BaseQueryParams &
  (
    | {
        sourceModelClass: 'email_file';
        sourceEmailFileId: string;
        sourcePartFileId?: never;
      }
    | {
        sourceModelClass: 'part_file';
        sourceEmailFileId?: never;
        sourcePartFileId: string | string[];
      }
  ) &
  (
    | {
        destinationModelClass: 'Quote';
        destinationQuoteId: string;
        destinationPartId?: never;
      }
    | {
        destinationModelClass: 'Part';
        destinationQuoteId?: never;
        destinationPartId: string;
      }
  );

export const SUGGESTIONS_BASE_QUERY_KEY = ['suggestions', 'list'];

export const getUseGetSuggestionsKey = (query?: GetSuggestionsQueryParams) =>
  [...SUGGESTIONS_BASE_QUERY_KEY, query] as const;

export const invalidateFieldSuggestionsKeyForQuoteId = (quoteId: number) => {
  queryClient.invalidateQueries({
    queryKey: ['suggestions', 'list', { destinationQuoteId: quoteId }],
  });
};

export const useGetSuggestions: QueryFunction<
  FieldSuggestion[],
  [],
  ReturnType<typeof getUseGetSuggestionsKey>,
  GetSuggestionsQueryParams
> = ({ queryOptions, requestOptions, query } = {}) => {
  const request = useRequest(requestOptions);
  if (query && typeof query !== 'string') {
    query.destinationModelClass = query.destinationModelClass || 'Part';
  }

  return useQuery({
    ...queryOptions,
    queryKey: getUseGetSuggestionsKey(query),
    queryFn: ({ signal }) => {
      return request<FieldSuggestion[]>(`/suggestions`, {
        query,
        init: {
          signal,
        },
      });
    },
  });
};
